<div class="mx-auto grid max-w-[1400px] gap-5 p-4 text-slate-900">

  <div class="grid gap-4">
    <a routerLink="/energy-admin" class="text-[13px] text-slate-600">&larr; Admin</a>
    <h1 class="m-0 text-xl font-bold">Standort-Einstellungen</h1>
  </div>

  <!-- ── Auswahl ──────────────────────────────────────────────────────────── -->
  <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
    <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
      <p class="m-0 text-[13px] leading-relaxed text-slate-600">
        <code>location.metadata</code> des Standorts. Leere Felder werden beim Speichern entfernt; Speichern ersetzt
        das ganze Objekt.
      </p>
      <span class="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
        <span class="rounded-full bg-slate-900/5 px-2.5 py-[3px]">GET / PUT <code>{{ settingsPath }}</code></span>
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <mat-form-field appearance="outline" class="min-w-[260px]" subscriptSizing="dynamic">
        <mat-label>Standort</mat-label>
        <mat-select [value]="selectedLocationId()" (valueChange)="setLocationId($event)" [disabled]="isBusy()">
          @for (loc of locationState.locations(); track loc.id) {
            <mat-option [value]="loc.id">{{ loc.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <input
        type="search"
        placeholder="Schlüssel oder Beschreibung suchen"
        class="min-w-[240px] flex-1 rounded-[10px] border border-slate-900/20 px-2.5 py-2 text-sm"
        [ngModel]="search()"
        (ngModelChange)="search.set($event)"
      />

      <!-- Gruppierung ausgeblendet; zum Einblenden "hidden" entfernen. -->
      <div class="hidden overflow-hidden rounded-[10px] border border-slate-900/20 text-[13px]" role="group"
           aria-label="Gruppierung">
        <button type="button" class="px-3 py-2" [class.bg-slate-900]="grouping() === 'category'"
                [class.text-white]="grouping() === 'category'" (click)="grouping.set('category')">
          Kategorie
        </button>
        <button type="button" class="px-3 py-2" [class.bg-slate-900]="grouping() === 'suggested'"
                [class.text-white]="grouping() === 'suggested'" (click)="grouping.set('suggested')">
          Vorgeschlagen
        </button>
      </div>
    </div>

    <div class="mt-3 flex flex-wrap items-center gap-1.5 text-[12px]">
      @for (category of categories; track category.id) {
        @if (categoryCounts()[category.id]; as count) {
          <button
            type="button"
            class="rounded-full px-2.5 py-1 font-medium"
            [ngClass]="category.badge"
            [class.opacity-40]="hiddenCategories().has(category.id)"
            [attr.aria-pressed]="!hiddenCategories().has(category.id)"
            (click)="toggleCategory(category.id)">
            {{ category.label }} <span class="font-normal">{{ count.set }}/{{ count.total }}</span>
          </button>
        }
      }
    </div>
  </section>

  <!-- ── Geo-Position ─────────────────────────────────────────────────────── -->
  @if (settings()) {
    <details class="group rounded-2xl border border-slate-900/10 bg-white shadow-sm">
      <summary class="flex cursor-pointer list-none flex-wrap items-center gap-2 p-4 text-[13px] [&::-webkit-details-marker]:hidden">
        <span class="inline-block transition-transform group-open:rotate-90" aria-hidden="true">&#9656;</span>
        <span class="text-sm font-bold">Geo-Position</span>
        <span class="text-slate-500">
          {{ geoOriginal().latitude || '–' }}, {{ geoOriginal().longitude || '–' }} · {{ geoOriginal().timezone || '–' }}
        </span>
        @if (geoChanged()) {
          <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-900">geändert</span>
        }
      </summary>

      <div class="grid gap-3 border-t border-slate-100 p-4 text-[13px]">
        <p class="m-0 text-slate-600">
          Gespeichert in der Spalte <code>geo</code> des Standorts, nicht in <code>metadata</code>. Wetter (Open-Meteo)
          und Prognose lesen die Position von dort; leere Felder werden entfernt.
          <span class="ml-1 rounded-full bg-slate-900/5 px-2.5 py-[3px] text-[11px] text-slate-500">PUT <code>{{ geoPath }}</code></span>
        </p>

        <div class="grid gap-3 sm:grid-cols-3">
          @for (field of geoFields; track field.key) {
            <label class="grid gap-1">
              <span class="font-semibold">{{ field.label }} <span class="font-normal text-slate-500">{{ field.unit }}</span></span>
              <input
                class="w-full rounded-md border px-2 py-1 font-mono text-[12px]"
                [ngClass]="geoErrors()[field.key] ? 'border-red-500' : isGeoChanged(field.key) ? 'border-amber-500 bg-amber-50' : 'border-slate-900/20'"
                [attr.inputmode]="field.limit !== null ? 'decimal' : null"
                [placeholder]="field.hint"
                [ngModel]="geoDraftOf(field.key)"
                (ngModelChange)="setGeoDraft(field.key, $event)"
                [disabled]="isBusy() || geoPhase() === 'saving'"
              />
              @if (geoErrors()[field.key]; as error) {
                <span class="text-[11px] text-red-700">{{ error }}</span>
              } @else if (isGeoChanged(field.key)) {
                <span class="text-[11px] text-slate-500">vorher: <code>{{ geoOriginal()[field.key] || '(leer)' }}</code></span>
              } @else {
                <span class="text-[11px] text-slate-500">{{ field.hint }}</span>
              }
            </label>
          }
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button type="button" class="rounded-[10px] bg-slate-900 px-3 py-1.5 font-semibold text-white disabled:opacity-40"
                  [disabled]="!geoCanSave()" (click)="saveGeo()">
            Geo-Position speichern
          </button>
          <button type="button" class="rounded-[10px] border border-slate-400 px-3 py-1.5 disabled:opacity-40"
                  [disabled]="!geoChanged() || geoPhase() === 'saving'" (click)="resetGeo()">
            Verwerfen
          </button>
          <span class="ml-auto" role="status" aria-live="polite">
            @switch (geoPhase()) {
              @case ('saving') { Speichert… }
              @case ('saved') { <span class="text-emerald-700">{{ geoMessage() }}</span> }
              @case ('error') { <span class="text-red-700">{{ geoMessage() }}</span> }
            }
          </span>
        </div>
      </div>
    </details>

    <!-- ── Tarife ──────────────────────────────────────────────────────────── -->
    <app-location-tariffs [locationId]="settings()!.locationId" />
  }

  <!-- ── Speichern ────────────────────────────────────────────────────────── -->
  <section class="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-900/10 bg-white/95 p-3 text-[13px] shadow-sm backdrop-blur">
    <button type="button" class="rounded-[10px] bg-slate-900 px-3 py-1.5 font-semibold text-white disabled:opacity-40"
            [disabled]="!canSave()" (click)="save()">
      Speichern
    </button>
    <button type="button" class="rounded-[10px] border border-slate-400 px-3 py-1.5 disabled:opacity-40"
            [disabled]="isBusy() || changedKeys().length === 0" (click)="resetAll()">
      Verwerfen
    </button>
    @if (unknownCount() > 0) {
      <button type="button" class="rounded-[10px] border border-red-300 px-3 py-1.5 text-red-800 disabled:opacity-40"
              [disabled]="isBusy()" (click)="clearUnknown()">
        Unbekannte Schlüssel entfernen ({{ unknownCount() }})
      </button>
    }

    <span class="text-slate-600">
      {{ changedKeys().length }} geändert
      @if (invalidKeys().length > 0) {
        · <span class="font-semibold text-red-700">{{ invalidKeys().length }} ungültig</span>
      }
    </span>

    <span class="ml-auto" role="status" aria-live="polite">
      @switch (phase()) {
        @case ('loading') { Lädt… }
        @case ('saving') { Speichert… }
        @case ('saved') { <span class="text-emerald-700">{{ message() }}</span> }
        @case ('error') { <span class="text-red-700">{{ message() }}</span> }
      }
    </span>
  </section>

  <!-- ── Tabelle ──────────────────────────────────────────────────────────── -->
  @if (settings()) {
    <section class="overflow-x-auto rounded-2xl border border-slate-900/10 bg-white shadow-sm">
      <table class="w-full min-w-[960px] border-collapse text-[13px]">
        <thead class="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th class="px-3 py-2 font-semibold">Schlüssel</th>
            @if (grouping() === 'category') {
              <th class="px-3 py-2 font-semibold">Thema</th>
            }
            <th class="px-3 py-2 font-semibold">Beschreibung</th>
            <th class="w-[220px] px-3 py-2 font-semibold">Wert</th>
            <th class="px-3 py-2 font-semibold">Einheit</th>
            <th class="px-3 py-2"><span class="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          @for (group of groups(); track group.id) {
            <tr class="border-t border-slate-200 bg-slate-100/70">
              <th [attr.colspan]="columnCount()" class="px-3 py-1.5 text-left text-[12px] font-bold">
                {{ group.label }} <span class="font-normal text-slate-500">{{ group.rows.length }}</span>
              </th>
            </tr>
            @for (row of group.rows; track row.key) {
              <tr class="border-t border-slate-100 align-top"
                  [class.bg-amber-50]="isChanged(row) || !!row.movesTo"
                  [class.bg-red-50]="!row.field && !row.movesTo && !isChanged(row)">
                <td class="whitespace-nowrap px-3 py-2"><code>{{ row.key }}</code></td>
                @if (grouping() === 'category') {
                  <td class="whitespace-nowrap px-3 py-2 text-[12px] text-slate-600">
                    {{ topicLabel(row.suggestedCategory) }}
                  </td>
                }
                <td class="px-3 py-2 text-slate-700">
                  @if (row.field) {
                    {{ row.field.description }}
                    @if (row.field.note) {
                      <div class="mt-0.5 text-[12px] text-orange-800">{{ row.field.note }}</div>
                    }
                  } @else {
                    @if (row.movesTo) {
                      <span class="text-amber-900">Noch nicht verschoben, gehört nach <code>{{ row.movesTo }}</code>;
                        das Backend liest ihn hier noch als Rückfall, ein Wert dort hat Vorrang. Verschiebt
                        <code>location_metadata_move_to_categories.sql</code>.</span>
                    } @else {
                      <span class="text-red-800">Kein Code liest diesen Schlüssel.</span>
                    }
                  }
                </td>
                <td class="px-3 py-2">
                  @switch (row.field?.type) {
                    @case ('BOOLEAN') {
                      <select class="w-full rounded-md border border-slate-900/20 px-2 py-1"
                              [ngModel]="draftOf(row)" (ngModelChange)="setDraft(row.key, $event)" [disabled]="isBusy()">
                        <option value="">(leer: aus)</option>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    }
                    @case ('ENUM') {
                      <select class="w-full rounded-md border border-slate-900/20 px-2 py-1"
                              [ngModel]="draftOf(row)" (ngModelChange)="setDraft(row.key, $event)" [disabled]="isBusy()">
                        <option value="">(leer)</option>
                        @for (value of row.field!.allowedValues ?? []; track value) {
                          <option [value]="value">{{ value }}</option>
                        }
                      </select>
                    }
                    @default {
                      <input
                        class="w-full rounded-md border px-2 py-1 font-mono text-[12px]"
                        [ngClass]="errorOf(row) ? 'border-red-500' : 'border-slate-900/20'"
                        [attr.inputmode]="row.field ? 'decimal' : null"
                        [placeholder]="row.field ? '(leer)' : 'JSON'"
                        [ngModel]="draftOf(row)"
                        (ngModelChange)="setDraft(row.key, $event)"
                        [disabled]="isBusy() || !!row.movesTo"
                      />
                    }
                  }
                  @if (errorOf(row); as error) {
                    <div class="mt-0.5 text-[11px] text-red-700">{{ error }}</div>
                  } @else if (isChanged(row)) {
                    <div class="mt-0.5 text-[11px] text-slate-500">
                      vorher: <code>{{ row.originalText || '(leer)' }}</code>
                    </div>
                  }
                </td>
                <td class="whitespace-nowrap px-3 py-2 text-slate-600">{{ row.field?.unit }}</td>
                <td class="whitespace-nowrap px-3 py-2 text-right">
                  @if (isChanged(row)) {
                    <button type="button" class="text-[12px] underline" (click)="revert(row)">Zurück</button>
                  } @else if (draftOf(row) !== '' && !row.movesTo) {
                    <button type="button" class="text-[12px] text-slate-500 underline" (click)="clear(row)">
                      {{ row.field ? 'Leeren' : 'Entfernen' }}
                    </button>
                  }
                </td>
              </tr>
            }
          } @empty {
            <tr>
              <td [attr.colspan]="columnCount()" class="px-3 py-6 text-center text-slate-500">Keine Schlüssel passen zum Filter.</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  } @else if (phase() === 'error') {
    <section class="rounded-2xl border border-red-200 bg-red-50 p-4 text-[13px] text-red-800">{{ message() }}</section>
  }
</div>
